import _ from 'lodash';
import React from 'react';
import {useMemo} from 'react';

import * as Panel2 from './panel';
import {Spec as PanelTableSpec, PanelTableConfig} from './PanelTable';
import {PanelComp2} from './PanelComp';
import * as Types from './types';
import * as CG from './cg/graph';
import * as Op from './cg/ops';
import * as LLReact from './cg/cgreact';

import {Dropdown} from 'semantic-ui-react';

const inputType = {
  type: 'union' as const,
  members: [
    {
      type: 'file' as const,
      wbObjectType: 'joined-table' as const,
    },
    {
      type: 'array' as const,
      objectType: {
        type: 'file' as const,
        wbObjectType: 'joined-table' as const,
      },
    },
  ],
};

interface WBJoinedTable {
  table1: string;
  table2: string;
  join_key: string | null;
}

type PanelWBJoinedTableFileProps = Panel2.PanelProps<
  typeof inputType,
  PanelTableConfig
>;

type JoinStyle = 'left' | 'right' | 'outer' | 'inner';

const PanelWBJoinedTableFile: React.FC<PanelWBJoinedTableFileProps> = props => {
  const [leftJoinKey, setLeftJoinKey] = React.useState<string | null>(null);
  const [rightJoinKey, setRightJoinKey] = React.useState<string | null>(null);
  const [leftJoinKeyOptions, setLeftJoinKeyOptions] = React.useState<{
    [key: string]: string;
  } | null>(null);
  const [rightJoinKeyOptions, setRightJoinKeyOptions] = React.useState<{
    [key: string]: string;
  } | null>(null);
  const [joinStyle, setJoinStyle] = React.useState<JoinStyle>('inner');
  const [comparisonJoinStyle, setComparisonJoinStyle] = React.useState<
    JoinStyle
  >('outer');

  const fileOrFiles = props.input.path;
  const tablesRowsWithUnknownType = useMemo(() => {
    const tables = Op.applyOpToOneOrMany(
      Op.opFileJoinedTable,
      'file',
      fileOrFiles,
      {}
    );
    return Op.applyOpToOneOrManyUnsafe(
      Op.opJoinedTableRows,
      'joinedTable',
      tables,
      {
        leftOuter: Op.constBoolean(['outer', 'left'].includes(joinStyle)),
        rightOuter: Op.constBoolean(['outer', 'right'].includes(joinStyle)),
      }
    );
  }, [fileOrFiles, joinStyle]);
  const tableWithTypeProm = LLReact.useNodeWithServerType(
    tablesRowsWithUnknownType as any
  );
  const tablesRows = tableWithTypeProm.result;

  const validRightKeys = React.useCallback(() => {
    if (
      leftJoinKey !== null &&
      leftJoinKeyOptions !== null &&
      rightJoinKeyOptions !== null
    ) {
      return Object.entries(rightJoinKeyOptions)
        .filter(entry => entry[1] === leftJoinKeyOptions[leftJoinKey])
        .map(entry => entry[0]);
    }
    return [];
  }, [leftJoinKey, leftJoinKeyOptions, rightJoinKeyOptions]);

  const finalRows = useMemo(() => {
    if (tableWithTypeProm.loading) {
      return tablesRows;
    }
    if (fileOrFiles.type.type === 'array') {
      // NOTE: we're not joining the values in the array as an op! we're reaching
      // into ops and grabbing their inputs... this doesn't seem right.
      if (tablesRows.nodeType !== 'output') {
        throw new Error('invalid');
      }
      const tablesRowsType = tablesRows.type;
      if (
        Types.isSimpleType(tablesRowsType) ||
        tablesRowsType.type !== 'array'
      ) {
        throw new Error('invalid');
      }
      // TODO: This is really gnarly and bad. We take the type out of the input array
      // op (which we resolved from the server) and apply it to both arrays
      // Also I believe this will throw an error if the tables don't have the same
      // exact columns.
      const objType = tablesRowsType.objectType;

      if (leftJoinKeyOptions === null) {
        setLeftJoinKeyOptions(
          _.fromPairs(
            Object.entries(
              tablesRows.fromOp.inputs['0'].type.objectType.propertyTypes[0]
                .members[1].propertyTypes as {[key: string]: string}
            ).filter(entry => Types.SORTABLE_TYPES.includes(entry[1]))
          )
        );
      }
      if (rightJoinKeyOptions === null) {
        setRightJoinKeyOptions(
          tablesRows.fromOp.inputs['1'].type.objectType.propertyTypes[0]
            .members[1].propertyTypes
        );
      }
      if (leftJoinKey === null && leftJoinKeyOptions !== null) {
        const leftKeys = Object.keys(leftJoinKeyOptions);
        if (leftKeys.length > 0) {
          setLeftJoinKey(leftKeys[0]);
        }
      }
      const validRightKeysInner = validRightKeys();
      if (rightJoinKey === null && validRightKeysInner.length > 0) {
        setRightJoinKey(validRightKeysInner[0]);
      }
      console.log(leftJoinKeyOptions, rightJoinKeyOptions);
      if (leftJoinKey != null && rightJoinKey != null) {
        const arr1 = {...tablesRows.fromOp.inputs['0']};
        const arr2 = {...tablesRows.fromOp.inputs['1']};
        return Op.opJoin({
          arr1: arr1 as any,
          arr2: arr2 as any,
          join1Fn: Op.constFunction(
            Op.opPick({
              obj: CG.varNode(objType, 'x') as any,
              key: Op.constString('0.' + leftJoinKey),
            })
          ) as any,
          join2Fn: Op.constFunction(
            Op.opPick({
              obj: CG.varNode(objType, 'x') as any,
              key: Op.constString('0.' + leftJoinKey),
            })
          ) as any,
          alias1: Op.constString('0'),
          alias2: Op.constString('1'),
          leftOuter: Op.constBoolean(
            ['outer', 'left'].includes(comparisonJoinStyle)
          ),
          rightOuter: Op.constBoolean(
            ['outer', 'right'].includes(comparisonJoinStyle)
          ),
        });
      }
      return null;
    } else {
      setLeftJoinKeyOptions(null);
      setRightJoinKeyOptions(null);
      return tablesRows;
    }
  }, [
    tablesRows,
    tableWithTypeProm.loading,
    fileOrFiles.type.type,
    leftJoinKey,
    rightJoinKey,
    validRightKeys,
    leftJoinKeyOptions,
    rightJoinKeyOptions,
    comparisonJoinStyle,
  ]);
  if (tableWithTypeProm.loading) {
    return <div></div>;
  }
  return (
    <>
      <div>
        <>
          Join sub-tables using a&nbsp;&nbsp;
          <Dropdown
            button
            options={[
              {
                text: 'outer join',
                value: 'outer',
              },
              {
                text: 'left join',
                value: 'left',
              },
              {
                text: 'right join',
                value: 'right',
              },
              {
                text: 'inner join',
                value: 'inner',
              },
            ]}
            value={joinStyle}
            onChange={(e, data) => {
              setJoinStyle(data.value as JoinStyle);
            }}
          />
        </>
        {leftJoinKeyOptions !== null && rightJoinKeyOptions !== null && (
          <>
            , then compare the joined tables by&nbsp;&nbsp;
            <Dropdown
              button
              options={[
                {
                  text: 'outer joining',
                  value: 'outer',
                },
                {
                  text: 'left joining',
                  value: 'left',
                },
                {
                  text: 'right joining',
                  value: 'right',
                },
                {
                  text: 'inner joining',
                  value: 'inner',
                },
              ]}
              value={comparisonJoinStyle}
              onChange={(e, data) => {
                setComparisonJoinStyle(data.value as JoinStyle);
              }}
            />
            left&nbsp;&nbsp;
            <Dropdown
              button
              options={Object.keys(leftJoinKeyOptions).map(o => {
                return {
                  text: '0.0.' + o,
                  value: o,
                };
              })}
              value={leftJoinKey || ''}
              onChange={(e, data) => {
                setLeftJoinKey((data.value as string) || null);
                setRightJoinKey(null);
              }}
            />
            on right&nbsp;&nbsp;
            <Dropdown
              button
              options={validRightKeys().map(o => {
                return {
                  text: '1.0.' + o,
                  value: o,
                };
              })}
              value={rightJoinKey || ''}
              onChange={(e, data) => {
                setRightJoinKey((data.value as string) || null);
              }}
            />
          </>
        )}
      </div>
      {finalRows && (
        <PanelComp2
          // TODO: This should be actual inputType, not allowed
          inputType={PanelTableSpec.inputType}
          input={{path: finalRows as any}}
          loading={false}
          panelSpec={PanelTableSpec}
          configMode={false}
          context={props.context}
          config={props.config}
          updateConfig={props.updateConfig}
          updateContext={props.updateContext}
        />
      )}
    </>
  );
};

export const Spec: Panel2.PanelSpec = {
  id: 'wb-joined-table',
  Component: PanelWBJoinedTableFile,
  inputType,
};
